import { Box, Container, IconButton, Typography, Grid, LinearProgress, Select, MenuItem, FormControl, Chip, Tabs, Tab, Stack } from '@mui/material'
import React, { useState, useEffect,useRef } from 'react'
import {
    ArrowBack as ArrowBackIcon,
    SelfImprovement as YogaIcon,
    CalendarMonth as AppointmentIcon,
    DirectionsRun as ActivityIcon,
    Chat as ChatIcon,
    Mood as MoodIcon,
    TrendingUp as TrendingUpIcon,
    AccessTime as TimeIcon,
    MenuBook as GratitudeIcon,
    MusicNote as MusicIcon,
    GraphicEq as SoundTherapyIcon,
    Assignment as AssessmentIcon,
    Podcasts as PodcastIcon,
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    Psychology as PsychologyIcon,
    MonitorHeart as MonitorHeartIcon,
    Close as CloseIcon,
    Headphones as HeadphonesIcon,
    FiberManualRecord as DotIcon,
    BarChart as BarChartIcon,
    CheckCircle as CheckCircleIcon,
    EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
// import PsychologyIcon from '@mui/icons-material/Psychology';
import { useNavigate } from 'react-router-dom';
import { getUserEventCount } from '../../service/insights';
import { getWellnessAssessments } from '../../service/wellnessAssessment';
import { getAssessmentHistory, getSubjectiveWellBeingHistory } from '../../service/assessment';
import InsightsHistoryDialog from "./InsightsHistoryDialog";
import InsightActivityAnalytics from "./InsightActivityAnalytics";


const tensionColor = (label = '') => {
    const l = label.toUpperCase();
    if (l === 'HIGH' || l === 'SEVERE') return { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' };
    if (l === 'MODERATE') return { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' };
    return { bg: '#dcfce7', text: '#15803d', border: '#86efac' };
};

const CircleProgress = ({ score, max, color }) => {
    const r = 22, circ = 2 * Math.PI * r;
    const pct = Math.min(1, (score || 0) / (max || 1));
    return (
        <svg width="60" height="60" viewBox="0 0 60 60" style={{ flexShrink: 0 }}>
            <circle cx="30" cy="30" r={r} fill="none" stroke="#f1f5f9" strokeWidth="5" />
            <circle cx="30" cy="30" r={r} fill="none" stroke={color} strokeWidth="5"
                strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
                strokeLinecap="round" transform="rotate(-90 30 30)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
            <text x="30" y="34" textAnchor="middle" fontSize="11" fontWeight="800" fill={color}>{score}</text>
        </svg>
    );
};

const AssessmentDetail = ({ a }) => (
    <Box sx={{ borderTop: '1px solid #f1f5f9' }}>
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Score bars */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {[
                    { label: 'Wellness', value: a.wellness_score, color: '#22c55e', bg: '#dcfce7', icon: '🌿' },
                    { label: 'Tension', value: a.tension_score, color: '#f59e0b', bg: '#fef3c7', icon: '⚡' },
                    { label: 'Energy', value: a.energy_score, color: '#8b5cf6', bg: '#ede9fe', icon: '✨' },
                ].map(item => (
                    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontSize: '0.85rem', minWidth: 60 }}>{item.icon} {item.label}</Typography>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress variant="determinate" value={item.value ?? 0}
                                sx={{ height: 8, borderRadius: 8, backgroundColor: '#f1f5f9', '& .MuiLinearProgress-bar': { background: item.color, borderRadius: 8 } }} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: item.color, minWidth: 38, textAlign: 'right' }}>{item.value}%</Typography>
                    </Box>
                ))}
            </Box>

            {/* Energy Reserve */}
            <Box sx={{ p: 2, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#0B2D6B', fontSize: '0.85rem' }}>🔋 Energy Reserve</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: a.battery_input >= 60 ? '#22c55e' : a.battery_input >= 30 ? '#f59e0b' : '#ef4444' }}>{a.battery_input}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={a.battery_input}
                    sx={{ height: 10, borderRadius: 10, backgroundColor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 10, background: 'linear-gradient(90deg, #22c55e, #14b8a6, #8b5cf6)' } }} />
            </Box>

            {/* Tension Map */}
            {a.tension_map?.length > 0 && (
                <Box>
                    <Typography sx={{ fontWeight: 700, color: '#0B2D6B', fontSize: '0.85rem', mb: 1 }}>🧠 Tension Areas</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {a.tension_map.map((t, i) => {
                            const tc = tensionColor(t.label);
                            return (
                                <Box key={i} sx={{ px: 1.5, py: 0.7, borderRadius: '20px', background: tc.bg, border: `1px solid ${tc.border}`, display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                    <DotIcon sx={{ fontSize: 8, color: tc.text }} />
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: tc.text }}>{t.part}</Typography>
                                    <Typography sx={{ fontSize: '0.68rem', color: tc.text, opacity: 0.8 }}>· {t.label}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Energy Drains */}
            {a.energy_drains?.length > 0 && (
                <Box>
                    <Typography sx={{ fontWeight: 700, color: '#0B2D6B', fontSize: '0.85rem', mb: 1 }}>⚠️ Energy Drains</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {a.energy_drains.map((d, i) => (
                            <Box key={i} sx={{ p: 1.5, borderRadius: '10px', background: 'linear-gradient(135deg, #fff7ed, #ffedd5)', border: '1px solid #fed7aa', display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
                                <Typography sx={{ fontSize: '0.85rem', mt: 0.1 }}>⚠</Typography>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, color: '#c2410c', fontSize: '0.82rem' }}>{d.label}</Typography>
                                    <Typography sx={{ color: '#92400e', fontSize: '0.78rem', lineHeight: 1.4, mt: 0.2 }}>{d.insight}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Recommended Tracks */}
            {a.recommended_tracks?.length > 0 && (
                <Box>
                    <Typography sx={{ fontWeight: 700, color: '#0B2D6B', fontSize: '0.85rem', mb: 1 }}>🎵 Recommended Tracks</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {a.recommended_tracks.map((t, i) => (
                            <Box key={i} sx={{ p: 1.5, borderRadius: '10px', background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', border: '1px solid #ddd6fe', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <HeadphonesIcon sx={{ color: '#fff', fontSize: 18 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#4c1d95', fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</Typography>
                                    <Typography sx={{ color: '#7c3aed', fontSize: '0.72rem', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subtitle}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

        </Box>
    </Box>
);

const Insights = () => {
    const navigate = useNavigate();
    const userId = localStorage.getItem("user_id");
    const [timeFilter, setTimeFilter] = useState('weekly');
    const [eventCount, setEventCount] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('insights');
    // const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('insights_tab') || 'insights');
    const [stressData, setStressData] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [assessmentTab, setAssessmentTab] = useState('subjective');
    const [assessmentHistory, setAssessmentHistory] = useState([]);
    const [assessmentLoading, setAssessmentLoading] = useState(false);
    const [expandedAssessmentId, setExpandedAssessmentId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const tabsRef = useRef(null);
    
    const [selectedActivity, setSelectedActivity] = useState(null);  
    const [insights, setInsights] = useState({
        yogaVideos: { count: 0, trend: 0, goal: 10 },
        appointments: { count: 0, upcoming: 0 },
        activities: { count: 0, trend: 0, goal: 20 },
        counselorChats: { count: 0, avgDuration: 0 },
        moodCheckins: { count: 0, avgMood: 0 },
        timeSpent: { hours: 0, minutes: 0, totalMinutes: 0 },
        gratitudeJournal: { count: 0, streak: 0 },
        careMusic: { count: 0, totalMinutes: 0 },
        soundTherapy: { count: 0, avgDuration: 0 },
        assessments: { count: 0, completed: 0 },
        podcasts: { count: 0, totalMinutes: 0 },
        care: { count: 0 },

    });

    useEffect(() => {
        const fetchAssessmentHistory = async () => {
            setAssessmentLoading(true);
            try {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                if (user.id) {
                    const res = assessmentTab === 'subjective'
                        ? await getSubjectiveWellBeingHistory(user.id)
                        : await getAssessmentHistory(user.id);
                    const raw = res?.data?.data || [];
                    const parsed = raw
                        .map((item, idx) => ({
                            cycle: idx + 1,
                            score: assessmentTab === 'subjective' ? (item.total_score || 0) : (item.composite || 0),
                            date: item.created_at || new Date().toISOString(),
                            question_scores: item.question_scores || {},
                            emotional: item.emotional || 0,
                            mental: item.mental || 0,
                            physical: item.physical || 0,
                            social: item.social || 0,
                            financial: item.financial || 0,
                        }))
                        .filter(i => i.score > 0)
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((item, idx) => ({ ...item, cycle: idx + 1 }))
                        .reverse();
                    setAssessmentHistory(parsed);
                }
            } catch (e) {
                setAssessmentHistory([]);
            } finally {
                setAssessmentLoading(false);
            }
        };
        if (activeTab === 'wellness') fetchAssessmentHistory();
    }, [assessmentTab, activeTab]);

    // useEffect(() => {
    //     const fetchInsights = async () => {
    //         const user = JSON.parse(localStorage.getItem('user') || '{}');
    //         if (user.id) {
    //             try {
    //                 setIsLoading(true);
    //                 const today = new Date();
    //                 const fromDate = new Date(today.setDate(today.getDate() - 7));
    //                 const toDate = new Date();

    //                 const response = await getUserEventCount(
    //                     user.id,
    //                     fromDate.toISOString().split('T')[0],
    //                     toDate.toISOString().split('T')[0]
    //                 );
    //                 setEventCount(response);

    //                 // Map API response to insights state
    //                 setInsights({
    //                     yogaVideos: {
    //                         count: response.event_counts?.['yoga'] || 0,
    //                         trend: 0,
    //                         goal: 10
    //                     },
    //                     appointments: {
    //                         count: response.event_counts?.['appointment-created'] || 0,
    //                         upcoming: 0
    //                     },
    //                     activities: {
    //                         count: response.event_counts?.['Quick action'] || 0,
    //                         trend: 0,
    //                         goal: 20
    //                     },
    //                     counselorChats: {
    //                         count: response.event_counts?.['converse'] || 0,
    //                         avgDuration: 0
    //                     },
    //                     moodCheckins: {
    //                         count: response.event_counts?.['mood_checkin'] || 0,
    //                         avgMood: 7
    //                     },
    //                     gratitudeJournal: {
    //                         count: response.event_counts?.['gratitude_journal'] || 0,
    //                         streak: 0
    //                     },
    //                     careMusic: {
    //                         count: response.event_counts?.['care'] || 0,
    //                         totalMinutes: 0
    //                     },
    //                     soundTherapy: {
    //                         count: response.event_counts?.['sound_therapy'] || 0,
    //                         avgDuration: 0
    //                     },
    //                     assessments: {
    //                         count: response.event_counts?.['wellness_assessment'] || 0,
    //                         completed: 0
    //                     },
    //                     podcasts: {
    //                         count: response.event_counts?.['podcast'] || 0,
    //                         totalMinutes: 0
    //                     },
    //                     care: { count: response.event_counts?.['care'] || 0 },
    //                     consult: { count: response.event_counts?.['consult'] || 0 },
    //                     login: { count: response.event_counts?.['login'] || 0 },
    //                     pageView: { count: response.event_counts?.['page_view'] || 0 },
    //                     timeSpent: {
    //                         hours: Math.floor((response.usage_seconds?.daily || 0) / 3600),
    //                         minutes: Math.floor(((response.usage_seconds?.daily || 0) % 3600) / 60),
    //                         totalMinutes: response.usage_seconds?.daily || 0
    //                     }
    //                 });
    //                 setIsLoading(false);
    //             } catch (error) {
    //                 console.error('Error fetching insights:', error);
    //                 setIsLoading(false);
    //             }
    //         }
    //     };

    //     fetchInsights();
    // }, []);

    useEffect(() => {
        const fetchInsights = async () => {
            const user = JSON.parse(localStorage.getItem('user') || '{}');

            if (!user.id) return;

            try {
                setIsLoading(true);

                const today = new Date();
                let fromDate = new Date();

                // 🔥 Handle filter logic
                if (timeFilter === "daily") {
                    fromDate.setDate(today.getDate() - 1);
                } else if (timeFilter === "weekly") {
                    fromDate.setDate(today.getDate() - 7);
                } else if (timeFilter === "monthly") {
                    fromDate.setMonth(today.getMonth() - 1);
                }

                const toDate = new Date();

                const response = await getUserEventCount(
                    user.id,
                    fromDate.toISOString().split('T')[0],
                    toDate.toISOString().split('T')[0]
                );

                setEventCount(response);

                // 🔥 Map API response
                setInsights({
                    yogaVideos: {
                        count: response.event_counts?.['yoga'] || 0,
                        trend: 0,
                        goal: 10
                    },
                    appointments: {
                        count: response.event_counts?.['appointment-created'] || 0,
                        upcoming: 0
                    },
                    activities: {
                        count: response.event_counts?.['Quick action'] || 0,
                        trend: 0,
                        goal: 20
                    },
                    counselorChats: {
                        count: response.event_counts?.['converse'] || 0,
                        avgDuration: 0
                    },
                    moodCheckins: {
                        count: response.event_counts?.['mood_checkin'] || 0,
                        avgMood: 7
                    },
                    gratitudeJournal: {
                        count: response.event_counts?.['gratitude_journal'] || 0,
                        streak: 0
                    },
                    careMusic: {
                        count: response.event_counts?.['care'] || 0,
                        totalMinutes: 0
                    },
                    soundTherapy: {
                        count: response.event_counts?.['sound_therapy'] || 0,
                        avgDuration: 0
                    },
                    assessments: {
                        count: response.event_counts?.['assessment_selection'] || 0,
                        completed: response.event_counts?.['wellbeing_assessment_completed'] || 0
                    },
                    podcasts: {
                        count: response.event_counts?.['podcast'] || 0,
                        totalMinutes: 0
                    },
                    care: { count: response.event_counts?.['care'] || 0 },
                    consult: { count: response.event_counts?.['consult'] || 0 },
                    login: { count: response.event_counts?.['login'] || 0 },
                    pageView: { count: response.event_counts?.['page_view'] || 0 },
                    timeSpent: {
                        hours: Math.floor((response.usage_seconds?.daily || 0) / 3600),
                        minutes: Math.floor(((response.usage_seconds?.daily || 0) % 3600) / 60),
                        totalMinutes: response.usage_seconds?.daily || 0
                    }
                });

            } catch (error) {
                console.error('Error fetching insights:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInsights();
    }, [timeFilter]); // 👈 VERY IMPORTANT


    useEffect(() => {

    const fetchStressData = async () => {

        try {

            const user = JSON.parse(localStorage.getItem('user') || '{}');

            if (!user.id) return;

            const response = await getWellnessAssessments(user.id);

            console.log("Stress Data:", response);

            if (response?.data?.length > 0) {

                setStressData(response.data || []);
            }

        } catch (error) {

            console.log(error);
        }
    };

    fetchStressData();

}, []);

    
    const usageSeconds =
        eventCount?.usage_seconds?.[timeFilter] ?? 0;

    const usageHours = Math.floor(usageSeconds / 3600);
    const usageMinutes = Math.floor((usageSeconds % 3600) / 60);


    const formatDate = (date) => {
        return date.toISOString().split("T")[0]; // YYYY-MM-DD
    };

 

const openActivityDrawer = (type) => {
    setSelectedActivity(type);
    setDrawerOpen(true);
};
 const tabs = [
    { label: 'Overview', value: 'insights' },
    { label: 'Wellness Assessment', value: 'wellness' },
    { label: 'Decode Your Stress', value: 'stress' },
    {label:'Activity Analytics', value: 'analytics'}
];
 const insightCards = [

        {
            title: 'Yoga Videos',
            onClick: () => openActivityDrawer('yoga'),
            icon: <YogaIcon sx={{ fontSize: 32 }} />,
            count: insights.yogaVideos.count,
            subtitle: 'Total videos watched',
            color: '#7C3AED',
            bgColor: '#F5F3FF',
            progress: (insights.yogaVideos.count / insights.yogaVideos.goal) * 100,
            progressLabel: `${insights.yogaVideos.count}/${insights.yogaVideos.goal} weekly goal`
        },

        {
            title: 'Appointments',
            onClick: () => openActivityDrawer('appointment'),
            icon: <AppointmentIcon sx={{ fontSize: 32 }} />,
            count: insights.appointments.count,
            subtitle: `${insights.appointments.upcoming} upcoming`,
            color: '#0891B2',
            bgColor: '#ECFEFF',
            info: 'Total completed'
        },
        {
            title: 'Activities Done',
            onClick: () => openActivityDrawer('activity'),
            icon: <ActivityIcon sx={{ fontSize: 32 }} />,
            count: insights.activities.count,
            subtitle: `${insights.activities.trend}% more than last week`,
            color: '#059669',
            bgColor: '#ECFDF5',
            progress: (insights.activities.count / insights.activities.goal) * 100,
            progressLabel: `${insights.activities.count}/${insights.activities.goal} monthly goal`
        },
        {
            title: 'Converse',
            onClick: () => openActivityDrawer('converse'),
            icon: <ChatIcon sx={{ fontSize: 32 }} />,
            count: insights.counselorChats.count,
            subtitle: `Avg. ${insights.counselorChats.avgDuration} min per session`,
            color: '#DC2626',
            bgColor: '#FEF2F2',
            info: 'Total sessions'
        },
        {
            title: 'Mood Check-ins',
            onClick: () => openActivityDrawer('mood'),
            icon: <MoodIcon sx={{ fontSize: 32 }} />,
            count: insights.moodCheckins.count,
            subtitle: `Average mood: ${insights.moodCheckins.avgMood}/10`,
            color: '#F59E0B',
            bgColor: '#FFFBEB',
            info: 'This month'
        },
        
        {
            title: 'Time Spent on App',
            icon: <TimeIcon sx={{ fontSize: 32 }} />,
            count: `${usageHours}h ${usageMinutes}m`,
            subtitle: 'This week',
            color: '#6366F1',
            bgColor: '#EEF2FF',
            info: 'Active time',
            customCount: true
        },

        {
            title: 'Gratitude Journal',
            onClick: () => openActivityDrawer('gratitude'),
            icon: <GratitudeIcon sx={{ fontSize: 32 }} />,
            count: insights.gratitudeJournal.count,
            subtitle: `${insights.gratitudeJournal.streak} day streak`,
            color: '#EC4899',
            bgColor: '#FCE7F3',
            info: 'Entries'
        },
        {
            title: 'Care Music',
           onClick: () => openActivityDrawer('careMusic'),
            icon: <MusicIcon sx={{ fontSize: 32 }} />,
            count: insights.careMusic.count,
            subtitle: `${insights.careMusic.totalMinutes} min total`,
            color: '#8B5CF6',
            bgColor: '#F3E8FF',
            info: 'Tracks played'
        },
        {
            title: 'Sound Therapy',
            onClick: () => openActivityDrawer('soundTherapy'),
            icon: <SoundTherapyIcon sx={{ fontSize: 32 }} />,
            count: insights.soundTherapy.count,
            subtitle: `Avg. ${insights.soundTherapy.avgDuration} min per session`,
            color: '#14B8A6',
            bgColor: '#CCFBF1',
            info: 'Sessions'
        },
        {
            title: 'Assessments',
            
            icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
            count: insights.assessments.count,
            subtitle: `${insights.assessments.completed} completed`,
            color: '#F97316',
            bgColor: '#FFEDD5',
            info: 'Total taken',
            
        },
        {
            title: 'Podcasts',
            onClick: () => openActivityDrawer('podcast'),
            icon: <PodcastIcon sx={{ fontSize: 32 }} />,
            count: insights.podcasts.count,
            subtitle: `${insights.podcasts.totalMinutes} min listened`,
            color: '#0EA5E9',
            bgColor: '#E0F2FE',
            info: 'Episodes'
        },
         {
            title: 'Decode Your Stress',
           icon: <PsychologyIcon sx={{ fontSize: 32 }} />,
           count: stressData.length,
            subtitle: `${stressData.length} assessments`,
            color: '#9390d3',
            bgColor: '#E0F2FE',
            info: 'Assessments'
        }



    ];
    
    const getStatusColor = (status = '') => {
        const s = status.toLowerCase();
        if (s.includes('well balanced') || s.includes('good')) return { dot: '#22c55e', bg: '#f0fdf4', text: '#15803d' };
        if (s.includes('moderate')) return { dot: '#f59e0b', bg: '#fffbeb', text: '#b45309' };
        if (s.includes('high') || s.includes('severe')) return { dot: '#ef4444', bg: '#fef2f2', text: '#b91c1c' };
        return { dot: '#8b5cf6', bg: '#f5f3ff', text: '#6d28d9' };
    };

    const isSubjective = assessmentTab === 'subjective';
    const getTierForAssessment = (score) => {
        if (isSubjective) {
            if (score >= 138) return { label: 'Excellent', color: '#22c55e' };
            if (score >= 102) return { label: 'Good', color: '#3b82f6' };
            if (score >= 66) return { label: 'Moderate', color: '#f59e0b' };
            return { label: 'Low', color: '#ef4444' };
        }
        if (score >= 80) return { label: 'Excellent', color: '#22c55e' };
        if (score >= 60) return { label: 'Good', color: '#3b82f6' };
        if (score >= 40) return { label: 'Fair', color: '#f59e0b' };
        return { label: 'Needs Improvement', color: '#ef4444' };
    };

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', mx: 'auto', mb: 2, animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                    <Typography sx={{ color: '#64748b', fontWeight: 500 }}>Loading your insights…</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ background: 'linear-gradient(160deg, #f8fffe 0%, #f5f3ff 60%, #fdf2f8 100%)', minHeight: '100vh' }}>
            <Container maxWidth="lg" sx={{ pt: 2, pb: 4 }}>

                {/* ── Page Header ── */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                        Insights
                    </Typography>
                    <IconButton onClick={() => navigate(-1)} sx={{ backgroundColor: '#F0F5FF', width: 40, height: 40, borderRadius: '50%', color: '#0B2D6B', '&:hover': { backgroundColor: '#E0EBFF' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                </Stack>

                {/* ── Tabs + Filter ── */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
 <Tabs
  ref={tabsRef}
  value={activeTab}
  onChange={(_, v) => setActiveTab(v)}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  textColor="primary"
  indicatorColor="primary"
  sx={{
    '& .MuiTabs-scrollButtons': {
      width: 22,
      minWidth: 22,
      color: '#64748b',
      opacity: 0.7,
    },

    '& .MuiTabs-scrollButtons .MuiSvgIcon-root': {
      fontSize: '0.9rem',
    },

    '& .MuiTab-root': {
      minWidth: 'fit-content',
      px: 2,
      whiteSpace: 'nowrap',
      textTransform: 'none',
    },
  }}
>

     
                        {tabs.map(tab => (
                            <Tab key={tab.value} value={tab.value} label={tab.label} />
                        ))}
                    </Tabs>
                    {(activeTab === 'insights' || activeTab === 'analytics') && (
                        <Box sx={{ pb: 1 }}>
                            <FormControl size="small">
                                <Select
                                    value={timeFilter}
                                    onChange={(e) => setTimeFilter(e.target.value)}
                                    sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0B2D6B', minWidth: 100, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }, '& .MuiSelect-select': { py: 0.7, px: 1.5 } }}
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </Box>

                {/* ══════════════════════════════════
                    TAB 1 — INSIGHTS
                ══════════════════════════════════ */}
                {activeTab === 'insights' && (
                    <>
                        {/* Hero Banner */}
                        <Box sx={{
                            mb: 3, p: { xs: 2, sm: 3 },
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, #22c55e 0%, #14b8a6 50%, #8b5cf6 100%)',
                            color: '#fff',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap:2,
                            boxShadow: '0 6px 30px rgba(139,92,246,0.2)',
                            overflow: 'hidden',
                            position: 'relative',
                        }}>
                            <Box sx={{ position: 'absolute', width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', top: -40, right: 60 }} />
                            <Box sx={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: -30, right: 20 }} />
                            <Box sx={{position: 'relative', zIndex: 1, flex: 1, minWidth: 0, maxWidth: '65%',}}>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem' , sm: '1.5rem' }, mb: 0.5 }}>Your Wellness Overview</Typography>
                                {/* <Typography sx={{ opacity: 0.85, fontSize: '0.88rem' }}>
                                    {timeFilter === 'daily' ? 'Last 24 hours' : timeFilter === 'weekly' ? 'Last 7 days' : 'Last 30 days'} snapshot
                                </Typography> */}
                                <Typography   sx={{fontSize: { xs: '0.85rem', sm: '1rem' }, lineHeight: 1.5}}>
                                    You're making excellent progress on your wellness journey. Stay consistent with your activities and mood check-ins for better insights.
                                </Typography>
                            </Box>
                            <Box sx={{
                                position: 'relative',
                                zIndex: 1,
                                textAlign: 'right',
                                flexShrink: 0,
                                minWidth: { xs: 160, sm: 180 },
                            }}>

  
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
    <Chip
      label={`${timeFilter === 'daily' ? 'Last 24 hours' : timeFilter === 'weekly' ? 'Last 7 days' : 'Last 30 days'} snapshot`}
      size="small"
      sx={{
        height: 24,
        borderRadius: '999px',
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.55)',
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        letterSpacing: '0.2px',
        '& .MuiChip-label': { px: 1.4 },
      }}
    />
  </Box>

             <Typography sx={{fontSize: {  xs: '1.8rem', sm: '2.4rem'}, fontWeight: 900,  whiteSpace: 'nowrap' , mt: 0.5,lineHeight: 1.1,}}> {`${usageHours}h ${usageMinutes}m`}</Typography>
              <Typography sx={{ opacity: 0.85, fontSize: '0.8rem' }}>
             Active time
              </Typography>

                   </Box>
                        </Box>

                        {/* Stat Cards */}
                        <Grid container spacing={2}>
                            {insightCards.map((card, index) => (
                                <Grid item xs={6} sm={4} md={4} key={index}>
                                    <Box
                                        onClick={card.onClick}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: '18px',
                                            background: '#fff',
                                            border: '1.5px solid #f1f5f9',
                                            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                            transition: 'all 0.25s ease',
                                            height: '100%',
                                            cursor: card.onClick ? 'pointer' : 'default',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: `0 8px 28px ${card.color}18`,
                                                transform: 'translateY(-3px)',
                                                border: `1.5px solid ${card.color}33`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                                            <Box sx={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: card.bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color }}>
                                                {React.cloneElement(card.icon, { sx: { fontSize: 24 } })}
                                            </Box>
                                            {card.info && (
                                                <Chip label={card.info} size="small" sx={{ml:1, fontSize: '0.65rem', fontWeight: 700, height: 20, background: card.bgColor, color: card.color, border: 'none' }} />
                                            )}
                                        </Box>
                                        <Typography sx={{ fontWeight: 800, fontSize: '1.9rem', color: card.color, lineHeight: 1, mb: 0.3 }}>
                                            {card.count}
                                        </Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
                                            {card.title}
                                        </Typography>
                                        {card.onClick && (
                                            <ChevronRightIcon sx={{ position: 'absolute', bottom: 12, right: 12, color: card.color, fontSize: 18, opacity: 0.6 }} />
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}

                {/* ══════════════════════════════════
                    TAB 2 — WELLNESS ASSESSMENT
                ══════════════════════════════════ */}
                {activeTab === 'wellness' && (
                        <Box>
                            {/* Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: 'linear-gradient(135deg, #22c55e, #14b8a6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MonitorHeartIcon sx={{ color: '#fff', fontSize: 24 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Wellness Assessment</Typography>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>Your assessment history</Typography>
                                </Box>
                            </Box>

                            {/* Sub-tabs */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 3, p: 0.5, background: '#f1f5f9', borderRadius: '14px', width: 'fit-content' }}>
                                {[
                                    { value: 'subjective', label: 'Subjective Well-Being', color: '#22c55e' },
                                    { value: 'wellness', label: 'Wellness Index', color: '#8b5cf6' },
                                ].map(tab => (
                                    <Box
                                        key={tab.value}
                                        onClick={() => setAssessmentTab(tab.value)}
                                        sx={{
                                            px: 2, py: 0.8, borderRadius: '10px', cursor: 'pointer', fontWeight: 700,
                                            fontSize: '0.82rem', transition: 'all 0.2s', whiteSpace: 'nowrap',
                                            background: assessmentTab === tab.value ? '#fff' : 'transparent',
                                            color: assessmentTab === tab.value ? tab.color : '#94a3b8',
                                            boxShadow: assessmentTab === tab.value ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                        }}
                                    >
                                        {tab.label}
                                    </Box>
                                ))}
                            </Box>

                            {/* List */}
                            {assessmentLoading ? (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography sx={{ color: '#94a3b8' }}>Loading...</Typography>
                                </Box>
                            ) : assessmentHistory.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <MonitorHeartIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                    <Typography sx={{ color: '#94a3b8', fontWeight: 600 }}>No assessments yet</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* Summary Banner */}
                                                                        <Box
                                        sx={{
                                            p: 2.5,
                                            borderRadius: '20px',
                                            background:
                                            'linear-gradient(135deg, #22c55e 0%, #14b8a6 50%, #8b5cf6 100%)',
                                            color: '#fff',
                                            boxShadow: '0 6px 30px rgba(139,92,246,0.2)',
                                            display: 'flex',
                                            gap: 2,
                                            flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                        }}
                                        >
                                        {[
                                            { label: 'Total Completed', value: assessmentHistory.length, color: '#fff', Icon: CheckCircleIcon },
                                            {
                                            label: 'Avg Score',
                                            color: '#fff',
                                            Icon: BarChartIcon,
                                            value: isSubjective
                                                ? `${Math.round(
                                                    assessmentHistory.reduce((s, i) => s + i.score, 0) /
                                                    assessmentHistory.length
                                                )} / 174`
                                                : Math.round(
                                                    assessmentHistory.reduce((s, i) => s + i.score, 0) /
                                                    assessmentHistory.length
                                                ),
                                            },
                                            {
                                            label: 'Latest',
                                            value: getTierForAssessment(assessmentHistory[0]?.score).label,
                                            color: '#fff',
                                            Icon: TrophyIcon,
                                            },
                                        ].map((stat) => (
                                            <Box
                                            key={stat.label}
                                            sx={{
                                                flex: 1,
                                               minWidth: { xs: 0, sm: 100 },
                                                textAlign: 'center',
                                            }}
                                            >
                                            <stat.Icon sx={{ fontSize: 22, color: '#fff', mb: 0.3 }} />

                                            <Typography
                                                sx={{
                                                fontWeight: 900,
                                                fontSize: '1.2rem',
                                                color: '#fff',
                                                lineHeight: 1,
                                                }}
                                            >
                                                {stat.value}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                fontSize: '0.7rem',
                                                color: 'rgba(255,255,255,0.85)',
                                                fontWeight: 600,
                                                mt: 0.3,
                                                }}
                                            >
                                                {stat.label}
                                            </Typography>
                                            </Box>
                                        ))}
                                        </Box>

                                    {/* Cards */}
                                    {assessmentHistory.map((item) => {
                                        const tier = getTierForAssessment(item.score);
                                        const max = isSubjective ? 174 : 100;
                                        const dt = new Date(item.date);
                                        const isExpanded = expandedAssessmentId === item.cycle;
                                        const dimensions = [
                                            { label: 'Emotional', value: item.emotional, color: '#ec4899' },
                                            { label: 'Mental', value: item.mental, color: '#8b5cf6' },
                                            { label: 'Physical', value: item.physical, color: '#22c55e' },
                                            { label: 'Social', value: item.social, color: '#3b82f6' },
                                            { label: 'Financial', value: item.financial, color: '#f59e0b' },
                                        ];
                                        return (
                                            <Box key={item.cycle} sx={{
                                                borderRadius: '16px', background: '#fff',
                                                border: `1.5px solid ${isExpanded ? tier.color + '55' : tier.color + '22'}`,
                                                boxShadow: isExpanded ? `0 8px 24px ${tier.color}22` : '0 2px 8px rgba(0,0,0,0.04)',
                                                transition: 'all 0.25s', overflow: 'hidden',
                                            }}>
                                                {/* Row */}
                                                <Box
                                                    onClick={() => !isSubjective && setExpandedAssessmentId(isExpanded ? null : item.cycle)}
                                                    sx={{
                                                        px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 2,
                                                        cursor: isSubjective ? 'default' : 'pointer',
                                                        '&:hover': !isSubjective ? { background: '#fafafa' } : {},
                                                    }}>
                                                    <CircleProgress score={item.score} max={max} color={tier.color} />

                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500, mb: 0.2 }}>
                                                            {isSubjective ? 'Subjective Well-Being' : 'Wellness Index'}
                                                        </Typography>
                                                        <Typography sx={{ fontWeight: 800, color: '#0B2D6B', fontSize: '1rem', lineHeight: 1.2 }}>
                                                            Assessment {item.cycle}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                                {dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1' }}>·</Typography>
                                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                                {dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                            </Typography>
                                                        </Box>
                                                        {isSubjective && (
                                                            <LinearProgress variant="determinate" value={(item.score / 174) * 100}
                                                                sx={{ mt: 1, height: 4, borderRadius: 4, backgroundColor: '#f1f5f9', '& .MuiLinearProgress-bar': { background: tier.color, borderRadius: 4 } }} />
                                                        )}
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ textAlign: 'center' }}>
                                                            <Box sx={{ px: 1.5, py: 0.6, borderRadius: '20px', background: tier.color + '18', border: `1px solid ${tier.color}44` }}>
                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: tier.color, whiteSpace: 'nowrap' }}>{tier.label}</Typography>
                                                            </Box>
                                                            {isSubjective && (
                                                                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', mt: 0.5 }}>{item.score} / 174</Typography>
                                                            )}
                                                        </Box>
                                                        {!isSubjective && (
                                                            <ChevronRightIcon sx={{ color: '#cbd5e1', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.25s' }} />
                                                        )}
                                                    </Box>
                                                </Box>

                                                {/* Expanded Dimension Breakdown (Wellness Index only) */}
                                                {!isSubjective && isExpanded && (
                                                    <Box sx={{ px: 2.5, pb: 2.5, borderTop: '1px solid #f1f5f9' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', mt: 2, mb: 1.5 }}>
                                                            Dimension Breakdown
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                                            {dimensions.map(dim => (
                                                                <Box key={dim.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', width: 72, flexShrink: 0 }}>{dim.label}</Typography>
                                                                    <Box sx={{ flex: 1, position: 'relative' }}>
                                                                        <Box sx={{ height: 8, borderRadius: 4, background: '#f1f5f9' }} />
                                                                        <Box sx={{ height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${dim.color}88, ${dim.color})`, width: `${dim.value}%`, position: 'absolute', top: 0, left: 0, transition: 'width 0.6s ease' }} />
                                                                    </Box>
                                                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: dim.color, width: 36, textAlign: 'right', flexShrink: 0 }}>{dim.value}%</Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b' }}>Composite Score</Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: tier.color }}>{item.score}</Typography>
                                                                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>/ 100</Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                )}

                {/* ══════════════════════════════════
    TAB 4 — ACTIVITY ANALYTICS
══════════════════════════════════ */}
{activeTab === 'analytics' && (

    <InsightActivityAnalytics timeFilter={timeFilter} />

)}

                {/* ══════════════════════════════════
                    TAB 3 — DECODE YOUR STRESS
                ══════════════════════════════════ */}
                {activeTab === 'stress' && (
                    <Box>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Box sx={{ width: 44, height: 44, borderRadius: '12px',  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PsychologyIcon sx={{ color: '#fff', fontSize: 24 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Decode Your Stress</Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>Tap any row to view full details</Typography>
                            </Box>
                        </Box>

                        {stressData.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <PsychologyIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                <Typography sx={{ color: '#94a3b8', fontWeight: 600 }}>No stress assessments yet</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {stressData.map((a, i) => {
                                    const sc = getStatusColor(a.wellness_status);
                                    const rowKey = `s-${i}`;
                                    const isOpen = expandedId === rowKey;
                                    return (
                                        <Box key={i} sx={{ borderRadius: '16px', background: '#fff', border: `1.5px solid ${isOpen ? '#8b5cf633' : '#f1f5f9'}`, boxShadow: isOpen ? '0 8px 24px rgba(139,92,246,0.1)' : '0 2px 8px rgba(0,0,0,0.04)', transition: 'all 0.25s', overflow: 'hidden' }}>
                                            <Box onClick={() => setExpandedId(isOpen ? null : rowKey)} sx={{ px: 2.5, py: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', '&:hover': { background: '#fafafa' } }}>
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 0.3 }}>Status</Typography>
                                                    <Typography sx={{ fontWeight: 800, color: '#0B2D6B', fontSize: '1rem' }}>{a.wellness_status}</Typography>
                                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.2 }}>
                                                        {new Date(a.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                        {' · '}
                                                        {new Date(a.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                                                    {[{ label: 'Wellness', val: a.wellness_score, color: '#22c55e' }, { label: 'Tension', val: a.tension_score, color: '#f59e0b' }, { label: 'Energy', val: a.energy_score, color: '#8b5cf6' }].map((s) => (
                                                        <Box key={s.label} sx={{ textAlign: 'center' }}>
                                                            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: s.color, lineHeight: 1 }}>{s.val}%</Typography>
                                                            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500, mt: 0.3 }}>{s.label}</Typography>
                                                        </Box>
                                                    ))}
                                                    <ChevronRightIcon sx={{ color: '#cbd5e1', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.25s' }} />
                                                </Box>
                                            </Box>
                                            {isOpen && <AssessmentDetail a={a} />}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                )}
<InsightsHistoryDialog
    open={drawerOpen}
    onClose={() => setDrawerOpen(false)}
    selectedActivity={selectedActivity}
    timeFilter={timeFilter}
/>
            </Container>
        </Box>
    );
}

export default Insights
